'use es6';

import { useCallback, useMemo } from 'react';
import { daysAgo } from 'foundations-components/transitional/utils/SimpleDate';
import { RANGE_TYPES } from 'reporting-data/constants/dateRangeTypes';
import { DAY, WEEK, MONTH, QUARTER } from 'reporting-data/constants/frequency';
import { DEFAULT_RANGE_TYPES } from 'reporting-ui-components/components/shared/constants/DateRangeConstants';
import { useUpgradeModal } from 'ui-addon-upgrades/ums/useUpgradeModal';
import FormattedMessage from 'I18n/components/FormattedMessage';
import DateRangePicker from 'reporting-ui-components/components/shared/DateRangePicker';
import AttributionUsageTracker, { ACTIONS, EVENTS } from '../../usageTracker';
import { PQL_KEYS } from './constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const SUPPORTED_RANGE_FOR_LITE = [DAY, WEEK, MONTH, QUARTER];
const SUPPORTED_RANGE_FOR_LIMITED_ACCESS = [WEEK, MONTH, QUARTER, RANGE_TYPES.THIS_YEAR, RANGE_TYPES.LAST_YEAR, RANGE_TYPES.CUSTOM, RANGE_TYPES.ROLLING];
export const isSupportedDateRangeType = ({
  rangeTypeKey,
  hasRevenueAttributionLimitedAccess
}) => {
  let supportedRange = SUPPORTED_RANGE_FOR_LITE;
  if (hasRevenueAttributionLimitedAccess) {
    supportedRange = SUPPORTED_RANGE_FOR_LIMITED_ACCESS;
  }
  return supportedRange.some(freq => rangeTypeKey.includes(freq));
};
export const getDateRangeOptions = ({
  hasRevenueAttributionLimitedAccess
}) => Object.keys(DEFAULT_RANGE_TYPES).filter(range => !range.includes('NEXT')).map(rangeTypeKey => ({
  rangeTypeKey,
  text: `DateRangePicker.RANGE_TYPES.text.${rangeTypeKey}`,
  help: `DateRangePicker.RANGE_TYPES.help.${rangeTypeKey}`,
  isLocked: !isSupportedDateRangeType({
    rangeTypeKey,
    hasRevenueAttributionLimitedAccess
  })
}));
export const handleDateChange = ({
  dateRangeOption,
  hasRevenueAttributionLimitedAccess,
  setDateRangeState,
  openUpgradeModal,
  screen
}) => {
  const isSupported = isSupportedDateRangeType({
    rangeTypeKey: dateRangeOption.rangeType,
    hasRevenueAttributionLimitedAccess
  });
  AttributionUsageTracker.track(EVENTS.REVENUE_ATTRIBUTION_CARD, {
    action: ACTIONS.CHANGED_DATE_RANGE,
    screen,
    dateRange: dateRangeOption.rangeType,
    isDateRangeSelectedSupported: isSupported
  });
  if (isSupported) {
    setDateRangeState(dateRangeOption);
  } else {
    openUpgradeModal();
  }
};
export const getMinCustomValue = ({
  hasRevenueAttributionLimitedAccess
}) => {
  if (!hasRevenueAttributionLimitedAccess) {
    return null;
  }

  // Three years
  return daysAgo(365 * 3);
};
export default function RevenueAttributionDateRangePicker({
  hasRevenueAttributionLimitedAccess,
  dateRange,
  setDateRangeState,
  screen
}) {
  const {
    openUpgradeModal
  } = useUpgradeModal(PQL_KEYS.DATE_RANGE);
  const onChange = useCallback(dateRangeOption => handleDateChange({
    dateRangeOption,
    hasRevenueAttributionLimitedAccess,
    setDateRangeState,
    openUpgradeModal,
    screen
  }), [hasRevenueAttributionLimitedAccess, openUpgradeModal, setDateRangeState, screen]);
  const dateRangeOptions = useMemo(() => getDateRangeOptions({
    hasRevenueAttributionLimitedAccess
  }), [hasRevenueAttributionLimitedAccess]);
  const wrapperProps = useMemo(() => !hasRevenueAttributionLimitedAccess ? {
    className: 'm-x-6',
    style: {
      marginTop: '-24px'
    }
  } : null, [hasRevenueAttributionLimitedAccess]);
  const minCustomValue = useMemo(() => getMinCustomValue({
    hasRevenueAttributionLimitedAccess
  }), [hasRevenueAttributionLimitedAccess]);
  return /*#__PURE__*/_jsxs("div", Object.assign({}, wrapperProps, {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: "components.revenueAttributionCard.dateRange"
    }), /*#__PURE__*/_jsx(DateRangePicker, Object.assign({
      hideEntireCurrentUnit: true,
      onChange: onChange,
      rangeTypeOverrides: dateRangeOptions,
      minCustomValue: minCustomValue
    }, dateRange))]
  }));
}